import React, { useCallback } from 'react';
import { Table, Button, Input, Space } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { FT164FormItem, T206FormData } from '../hooks/useT206Record';

interface Props {
  attendees: FT164FormItem[];
  isEditing: boolean;
  setField: <K extends keyof T206FormData>(field: K, value: T206FormData[K]) => void;
}

const Tab3CustomerAttendees: React.FC<Props> = ({ attendees, isEditing, setField }) => {
  const handleChange = useCallback((idx: number, key: keyof FT164FormItem, value: string) => {
    const updated = attendees.map((row, i) =>
      i === idx ? { ...row, [key]: value } : row
    );
    setField('customer_attendees', updated);
  }, [attendees, setField]);

  const handleAdd = useCallback(() => {
    setField('customer_attendees', [
      ...attendees,
      { ft164_10: '', ft164_11: '', ft164_13: '', ft164_14: '' },
    ]);
  }, [attendees, setField]);

  const handleDelete = useCallback((idx: number) => {
    setField('customer_attendees', attendees.filter((_, i) => i !== idx));
  }, [attendees, setField]);

  const columns = [
    {
      title: '序',
      dataIndex: 'ft164_02',
      width: 40,
      render: (v: number, _: unknown, idx: number) => v ?? idx + 1,
    },
    {
      title: '客戶人員姓名',
      dataIndex: 'ft164_10',
      width: 130,
      render: (v: string, _: unknown, idx: number) =>
        isEditing ? (
          <Input
            size="small"
            value={v}
            maxLength={20}
            onChange={e => handleChange(idx, 'ft164_10', e.target.value)}
          />
        ) : v,
    },
    {
      title: '職稱',
      dataIndex: 'ft164_11',
      width: 120,
      render: (v: string, _: unknown, idx: number) =>
        isEditing ? (
          <Input
            size="small"
            value={v}
            maxLength={30}
            onChange={e => handleChange(idx, 'ft164_11', e.target.value)}
          />
        ) : v,
    },
    {
      title: '客戶代碼',
      dataIndex: 'ft164_13',
      width: 110,
      render: (v: string, _: unknown, idx: number) =>
        isEditing ? (
          <Input
            size="small"
            value={v}
            maxLength={12}
            onChange={e => handleChange(idx, 'ft164_13', e.target.value)}
            status={!v && isEditing ? 'error' : undefined}
          />
        ) : v,
    },
    {
      title: '客戶名稱',
      dataIndex: 'ft164_14',
      render: (v: string, _: unknown, idx: number) =>
        isEditing ? (
          <Input
            size="small"
            value={v}
            maxLength={50}
            onChange={e => handleChange(idx, 'ft164_14', e.target.value)}
          />
        ) : v,
    },
    ...(isEditing
      ? [
          {
            title: '',
            width: 40,
            render: (_: unknown, __: unknown, idx: number) => (
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(idx)}
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      {isEditing && (
        <Space style={{ marginBottom: 8 }}>
          <Button size="small" icon={<PlusOutlined />} onClick={handleAdd}>
            新增客戶人員
          </Button>
        </Space>
      )}
      <Table
        columns={columns}
        dataSource={attendees.map((row, i) => ({ ...row, key: i }))}
        size="small"
        pagination={false}
        scroll={{ x: 600 }}
        locale={{ emptyText: '尚無客戶參加人員' }}
      />
    </div>
  );
};

export default Tab3CustomerAttendees;
